import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Download, FileUp, Upload, Lock } from "lucide-react";
import { useAttendanceCodeConfig } from "../../../../context/attendanceCodeConfigContext";
import Modal from "../../masters/Modal";
import Enterprise from "../../Enterprise";
import Masters from "../../masters/Masters";
import AttendanceCodeMappingPanel from "./AttendanceCodeMappingPanel";
import { usePermissions } from "../../../../rbac/PermissionsContext";

// Tab key (UI) -> canonical catalog screen key under "configuration".
// Keep this in sync with src/rbac/catalog.js -> configuration.screens.
const CONFIG_TAB_TO_SCREEN = {
  attendanceCodes: "attendance_codes",
  enterprise: "enterprise",
  masters: "masters",
};

const Configuration = ({ darkMode }) => {
  const {
    loading,
    error,
    fetchConfigurations,
    saveConfigurations,
    resetConfigurations,
    halfDayDivider,
    halfDayPrefix,
  } = useAttendanceCodeConfig();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [activeConfigTab, setActiveConfigTab] = useState("enterprise");
  // Local state for half-day export format (company-wise, synced from context after load)
  const [halfDayDividerLocal, setHalfDayDividerLocal] = useState(halfDayDivider ?? "");
  const [halfDayPrefixLocal, setHalfDayPrefixLocal] = useState(halfDayPrefix ?? "");

  const { hasScreenAccess, isAdmin } = usePermissions();

  // Per-tab visibility. A tab is shown only if the role has "view" access for
  // the matching catalog screen. Admins implicitly get everything via the
  // wildcard snapshot.
  const allowedTabs = useMemo(
    () =>
      [
        { key: "attendanceCodes", label: "Attendance Codes" },
        { key: "enterprise", label: "Enterprise" },
        { key: "masters", label: "Masters" },
      ].filter((tab) =>
        hasScreenAccess("configuration", CONFIG_TAB_TO_SCREEN[tab.key], "view")
      ),
    [hasScreenAccess]
  );

  // If the active tab is no longer allowed (e.g. after a permission refresh),
  // fall back to the first visible tab. This prevents an empty screen.
  useEffect(() => {
    if (allowedTabs.length === 0) return;
    const isActiveAllowed = allowedTabs.some((t) => t.key === activeConfigTab);
    if (!isActiveAllowed) setActiveConfigTab(allowedTabs[0].key);
  }, [allowedTabs, activeConfigTab]);

  const canEditAttendanceCodes =
    isAdmin || hasScreenAccess("configuration", "attendance_codes", "edit");
  const canExportAttendanceCodes =
    isAdmin || hasScreenAccess("configuration", "attendance_codes", "export");

  const CONFIG_TAB_HEADINGS = {
    enterprise: "Enterprise",
    masters: "Masters",
  };

  const defaultConfigs = [
    { meaning: "Present", originalCode: "PR", customCode: "PR", importCode: "PR", exportCode: "PR" },
    { meaning: "Loss of Pay", originalCode: "LOP", customCode: "LOP", importCode: "LOP", exportCode: "LOP" },
    { meaning: "Weekly Off", originalCode: "WO", customCode: "WO", importCode: "WO", exportCode: "WO" },
    { meaning: "Public Holiday", originalCode: "PH", customCode: "PH", importCode: "PH", exportCode: "PH" },
  ];

  // Sync half-day fields from context when they change (e.g. after fetch or save)
  useEffect(() => {
    setHalfDayDividerLocal(
      halfDayDivider != null && halfDayDivider !== "" ? halfDayDivider : ""
    );
    setHalfDayPrefixLocal(
      halfDayPrefix != null && halfDayPrefix !== "" ? halfDayPrefix : ""
    );
  }, [halfDayDivider, halfDayPrefix]);

  useEffect(() => {
    // Build local rows from mapping; but better to re-fetch actual list
    const load = async () => {
      const configs = await fetchConfigurations();
      console.log("Configuration.jsx - fetched configs", configs);
      // configs may be array of { meaning, originalCode, customCode } or full response
      const codesArray = Array.isArray(configs) ? configs : (configs?.codes || configs?.configurations || []);
      if (!codesArray || codesArray.length === 0) {
        // populate sensible defaults so UI is usable even if backend has no data
        setRows(
          defaultConfigs.map((c) => ({
            meaning: c.meaning,
            originalCode: (c.originalCode || "").toUpperCase(),
            customCode: (c.customCode || c.originalCode || "").toUpperCase(),
            importCode: (c.importCode || c.originalCode || "").toUpperCase(),
            exportCode: (c.exportCode || c.originalCode || "").toUpperCase(),
          }))
        );
      } else {
        setRows(
          codesArray.map((c) => ({
            meaning: c.meaning || "",
            originalCode: (c.originalCode || c.original || c.original_code || "").toUpperCase(),
            customCode: (
              c.customCode ||
              c.custom ||
              c.custom_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
            importCode: (
              c.importCode ||
              c.import_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
            exportCode: (
              c.exportCode ||
              c.export_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
          }))
        );
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (idx, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        [field]: String(value || "").toUpperCase(),
      };
      return next;
    });
  };

  const handleSave = async () => {
    if (!canEditAttendanceCodes) {
      toast.error("You do not have permission to save attendance codes.");
      return;
    }
    try {
      setSaving(true);
      // Validate non-empty and uniqueness of custom, import, and export codes
      const codes = rows.map((r) => ({
        meaning: r.meaning,
        customCode: (r.customCode || "").toUpperCase(),
        importCode: (r.importCode || "").toUpperCase(),
        exportCode: (r.exportCode || "").toUpperCase(),
      }));
      
      // Check for empty codes
      const emptyCustom = codes.find((c) => !c.customCode || c.customCode.trim() === "");
      if (emptyCustom) {
        toast.error(`Custom code for "${emptyCustom.meaning}" cannot be empty`);
        setSaving(false);
        return;
      }
      const emptyImport = codes.find((c) => !c.importCode || c.importCode.trim() === "");
      if (emptyImport) {
        toast.error(`Import code for "${emptyImport.meaning}" cannot be empty`);
        setSaving(false);
        return;
      }
      const emptyExport = codes.find((c) => !c.exportCode || c.exportCode.trim() === "");
      if (emptyExport) {
        toast.error(`Export code for "${emptyExport.meaning}" cannot be empty`);
        setSaving(false);
        return;
      }
      
      // Check for duplicates
      const duplicateCustom = codes
        .map((c) => c.customCode)
        .filter((v, i, a) => a.indexOf(v) !== i);
      if (duplicateCustom.length > 0) {
        toast.error(
          `Duplicate custom codes found: ${[...new Set(duplicateCustom)].join(", ")}`
        );
        setSaving(false);
        return;
      }
      const duplicateImport = codes
        .map((c) => c.importCode)
        .filter((v, i, a) => a.indexOf(v) !== i);
      if (duplicateImport.length > 0) {
        toast.error(
          `Duplicate import codes found: ${[...new Set(duplicateImport)].join(", ")}`
        );
        setSaving(false);
        return;
      }
      const duplicateExport = codes
        .map((c) => c.exportCode)
        .filter((v, i, a) => a.indexOf(v) !== i);
      if (duplicateExport.length > 0) {
        toast.error(
          `Duplicate export codes found: ${[...new Set(duplicateExport)].join(", ")}`
        );
        setSaving(false);
        return;
      }

      console.log("Configuration.jsx - saving codes payload", { codes: codes, halfDayDivider: halfDayDividerLocal, halfDayPrefix: halfDayPrefixLocal });
      const resp = await saveConfigurations({
        codes: codes,
        halfDayDivider: halfDayDividerLocal.trim() === "" ? null : halfDayDividerLocal.trim(),
        halfDayPrefix: halfDayPrefixLocal.trim() === "" ? null : halfDayPrefixLocal.trim(),
      });
      console.log("Configuration.jsx - save response", resp);

      // Validate server response contains saved codes; if not, refetch and verify
      const savedResponse = Array.isArray(resp)
        ? resp
        : resp?.data ?? resp?.codes ?? resp?.configurations ?? [];
      if (!savedResponse || savedResponse.length === 0) {
        console.warn(
          "Configuration.jsx - save response contained no saved codes, refetching"
        );
        const fresh = await fetchConfigurations();
        if (!fresh || fresh.length === 0) {
          toast.error("Save reported success but server did not return saved configurations. Please refresh the page.");
          setSaving(false);
          return;
        }
      }

      // Refresh local rows from server response (accept array or { data: [...] } shapes)
      const saved = Array.isArray(resp)
        ? resp
        : resp?.data ?? resp?.codes ?? resp?.configurations ?? [];
      if (saved && saved.length > 0) {
        setRows(
          saved.map((c) => ({
            meaning: c.meaning || "",
            originalCode: (
              c.originalCode ||
              c.original ||
              c.original_code ||
              ""
            ).toUpperCase(),
            customCode: (
              c.customCode ||
              c.custom ||
              c.custom_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
            importCode: (
              c.importCode ||
              c.import_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
            exportCode: (
              c.exportCode ||
              c.export_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
          }))
        );
      } else {
        // If server didn't return saved rows, refetch from provider
        const fresh = await fetchConfigurations();
        setRows(
          (fresh || []).map((c) => ({
            meaning: c.meaning || "",
            originalCode: (
              c.originalCode ||
              c.original ||
              c.original_code ||
              ""
            ).toUpperCase(),
            customCode: (
              c.customCode ||
              c.custom ||
              c.custom_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
            importCode: (
              c.importCode ||
              c.import_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
            exportCode: (
              c.exportCode ||
              c.export_code ||
              c.originalCode ||
              ""
            ).toUpperCase(),
          }))
        );
      }

      setSaving(false);
      toast.success("Configurations saved successfully");
    } catch (err) {
      setSaving(false);
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to save configurations";
      toast.error(errorMessage);
    }
  };

  const handleReset = async () => {
    if (!canEditAttendanceCodes) {
      toast.error("You do not have permission to reset attendance codes.");
      return;
    }
    setShowResetModal(true);
  };

  const confirmReset = async () => {
    setShowResetModal(false);
    if (!canEditAttendanceCodes) {
      toast.error("You do not have permission to reset attendance codes.");
      return;
    }
    try {
      setSaving(true);
      const resp = await resetConfigurations();
      console.log("Configuration.jsx - reset response", resp);
      const configs =
        Array.isArray(resp) ? resp : resp?.data ?? resp?.codes ?? resp?.configurations ?? [];
      setRows(
        configs.map((c) => ({
          meaning: c.meaning || "",
          originalCode: (
            c.originalCode ||
            c.original ||
            c.original_code ||
            ""
          ).toUpperCase(),
          customCode: (
            c.customCode ||
            c.custom ||
            c.custom_code ||
            c.originalCode ||
            ""
          ).toUpperCase(),
          importCode: (
            c.importCode ||
            c.import_code ||
            c.originalCode ||
            ""
          ).toUpperCase(),
          exportCode: (
            c.exportCode ||
            c.export_code ||
            c.originalCode ||
            ""
          ).toUpperCase(),
        }))
      );
      setSaving(false);
      toast.success("Configurations reset to defaults");
    } catch (err) {
      setSaving(false);
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to reset configurations";
      toast.error(errorMessage);
    }
  };

  const exportAttendanceCodesCsv = () => {
    if (!canExportAttendanceCodes) {
      toast.error("You do not have permission to export attendance codes.");
      return;
    }
    try {
      const headers = ["Meaning", "Default Code", "Import Code", "Export Code"];
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const lines = [
        headers.join(","),
        ...rows.map((r) =>
          [r.meaning, r.originalCode, r.importCode, r.exportCode].map(escape).join(",")
        ),
      ];
      const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "attendance_configuration_codes.csv";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Configuration exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const downloadAttendanceCodesTemplate = () => {
    if (!canExportAttendanceCodes) {
      toast.error("You do not have permission to download templates.");
      return;
    }
    try {
      const headers = ["Meaning", "Default Code", "Import Code", "Export Code"];
      const sample = ["Present", "PR", "PR", "PR"];
      const blob = new Blob([[headers.join(","), sample.join(",")].join("\r\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "attendance_codes_import_template.csv";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Template downloaded.");
    } catch {
      toast.error("Download failed.");
    }
  };

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-redhalo-red"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-[calc(100dvh-5rem)] overflow-hidden">
      <div
        className={`flex h-full flex-col ${
          darkMode ? "bg-[#1C1C1C] text-[#E0E0E0]" : "bg-redhalo-gray/30 text-gray-900"
        }`}
      >
        {error && (
          <div className="mb-4 rounded border border-red-400 bg-red-100 p-4 text-red-700">
            <p>{error?.message || String(error)}</p>
          </div>
        )}

        <div className="flex flex-1 min-h-0">
        {/* Sub Sidebar — sticky below main nav, in-flow (no overlap of main content) — matches Employee.jsx */}
        <aside
          className="sticky top-0 z-20 flex h-full min-h-0 w-56 shrink-0 flex-col overflow-hidden bg-white shadow-sm dark:bg-[#121212]"
        >
          <div className="rh-subnav-aside-head">
            <h4 className="invisible text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400" aria-hidden="true">
              Setup
            </h4>
          </div>
          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto border-t border-gray-200 p-2 dark:border-[#3C3C4C]">
            {allowedTabs.length === 0 ? (
              <div
                className={`mt-2 rounded-md border px-3 py-3 text-xs ${
                  darkMode
                    ? "border-[#3C3C4C] bg-[#181818] text-gray-400"
                    : "border-gray-200 bg-gray-50 text-gray-600"
                }`}
              >
                You do not have access to any configuration section. Please
                contact your administrator.
              </div>
            ) : (
              allowedTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveConfigTab(tab.key)}
                  className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                    activeConfigTab === tab.key
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                      : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))
            )}
          </div>

          <div className="shrink-0 p-2 pt-3">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-[#3C3C4C] dark:bg-[#121212]">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                Data tools
              </p>
              <p className="mt-1 text-xs leading-snug text-slate-600 dark:text-slate-400">
                Import, export, or download attendance templates.
              </p>
              <div className="mt-3 space-y-1">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100 dark:text-[#E0E0E0] dark:hover:bg-white/10"
                  onClick={() =>
                    toast(
                      "Prepare your file using the downloaded template, then use your attendance upload flow to import.",
                      { duration: 4500 }
                    )
                  }
                >
                  <FileUp className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Import
                </button>
                <button
                  type="button"
                  disabled={saving || rows.length === 0 || !canExportAttendanceCodes}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100 dark:text-[#E0E0E0] dark:hover:bg-white/10 ${
                    saving || rows.length === 0 || !canExportAttendanceCodes
                      ? "cursor-not-allowed opacity-45"
                      : ""
                  }`}
                  onClick={exportAttendanceCodesCsv}
                  title={
                    !canExportAttendanceCodes
                      ? "You do not have permission to export attendance codes"
                      : undefined
                  }
                >
                  <Upload className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Export
                </button>
                <button
                  type="button"
                  disabled={!canExportAttendanceCodes}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100 dark:text-[#E0E0E0] dark:hover:bg-white/10 ${
                    !canExportAttendanceCodes ? "cursor-not-allowed opacity-45" : ""
                  }`}
                  onClick={downloadAttendanceCodesTemplate}
                >
                  <Download className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Download
                </button>
              </div>
            </div>
          </div>
        </aside>
        {/* Tab Content — flex sibling; aside width reserved in layout (no pl- offset needed) — matches Employee.jsx */}
        <div className="flex min-w-0 flex-1 flex-col">
        <div className="h-full overflow-hidden bg-white dark:bg-[#1C1C1C]">
          <div className="configuration-content-container relative h-full flex flex-col overflow-hidden min-h-0">
              <div
                className={`min-h-0 flex-1 p-4 lg:p-6 ${
                  activeConfigTab === "masters" ? "overflow-hidden" : "overflow-y-auto"
                }`}
              >
                {allowedTabs.length === 0 && (
                  <div className="flex h-full items-center justify-center">
                    <div
                      className={`max-w-md rounded-xl border p-6 text-center ${
                        darkMode
                          ? "border-[#3C3C4C] bg-[#1f1f1f] text-gray-300"
                          : "border-gray-200 bg-white text-gray-700"
                      }`}
                    >
                      <h4 className="mb-1 text-sm font-semibold uppercase tracking-wide">
                        No configuration access
                      </h4>
                      <p className="text-xs">
                        You do not currently have permission to view any
                        section of Configuration. Please contact your
                        administrator to request access.
                      </p>
                    </div>
                  </div>
                )}

                {activeConfigTab === "enterprise" &&
                  hasScreenAccess("configuration", "enterprise", "view") && (
                    <Enterprise darkMode={darkMode} />
                  )}
                {activeConfigTab === "masters" &&
                  hasScreenAccess("configuration", "masters", "view") && (
                    (
                  <Masters
                    darkMode={darkMode}
                    initialTab="masters"
                    allowedTabs={["masters"]}
                  />
                )
                  )}

                {activeConfigTab === "attendanceCodes" &&
                  hasScreenAccess("configuration", "attendance_codes", "view") && (
                    <AttendanceCodeMappingPanel darkMode={darkMode} />
                  )}
              </div>

              <Modal
                darkMode={darkMode}
                isOpen={showResetModal}
                onClose={() => setShowResetModal(false)}
                title="Reset to Defaults"
                onSubmit={confirmReset}
                submitText="Reset"
                onCancel={() => setShowResetModal(false)}
              >
                <p className="mb-4">
                  Are you sure you want to reset all codes to defaults? This will discard all custom, import, and export codes, and reset half-day divider and prefix.
                </p>
              </Modal>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Configuration;
